import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Col, UncontrolledTooltip } from 'reactstrap';
import { DatePicker, GalleryTagsInput, InputItem, SelectItem } from '../../../components';
import { get, partial } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { FaPen, FaUndo } from 'react-icons/fa';
import { getBrandsList, getCollectionSettings, getUser } from '../../../modules/selectors';
import { getPin, updateCollectionByPath } from '../../../modules/actions/collections';
import DomainSelection from './settingsSection/DomainSelection';
import SwitchItem from '../../../components/Forms/SwitchItem';
import SelectWatermarkModal from '../imagesTabContent/modals/SelectWatermarkModal';
import theme from '../../../assets/css/theme';
import { getTranslationKey } from '../../../modules/utils/helpers';
import media from '../../../assets/css/media';
/* tslint:disable */
import moment from 'moment';
import { getMessages } from '../../../modules/selectors/message';
import { fetchMessagesAction } from '../../../modules/actions/message';
import { TurnOffFaceRecognitionModal } from '../../../components/TurnOffFaceRecognitionModal';
import {
  useCollectionSettingsFieldUpdate,
  useSettingsDownloadPin,
  useSettingsFaceRecognition,
  useSettingsGlobalDownload,
  useSettingsGlobalWatermark
} from '../../../hooks/settings';
import { DisableCommentsModal } from '../../../components/DisableCommentsModal';
import { useCommentsToggle } from './hooks/useCommentsToggle';

const Wrapper = styled(Col)`
  border-right: 10px solid ${theme.commonColors.secondBackground};
  background: ${theme.commonColors.mainBackground};
  padding-top: 15px;
  padding-bottom: 5px;
  @media (max-width: ${media.desktop}) {
    border-right: 0;
    margin-bottom: 20px;
  }
`;

const SwitchContainer = styled.div<{ isOn: boolean }>`
  display: flex;
  margin-right: 10px;
  align-items: center;

  svg {
    height: 10px;
    opacity: ${(props) => (props.isOn ? 1 : 0)};
  }
`;

const PenIcon = styled(FaPen)`
  cursor: pointer;
`;

export const languagesMap: { [key: number]: string } = {
  0: 'de',
  1: 'en',
  2: 'en'
};

export const PasswordInputLabel = () => {
  const { security } = useSelector(getCollectionSettings);
  const dispatch = useDispatch();

  return (
    <div className="d-flex justify-content-between w-100">
      <span>{getTranslationKey('collectionSettings.galleryPW')}</span>
      {!!security.passwordTriesLockedUntil && (
        <a
          href="/"
          style={{ cursor: 'pointer', marginRight: '0.7rem' }}
          onClick={(e) => {
            e.preventDefault();
            dispatch(
              updateCollectionByPath({
                path: 'security.passwordTriesLockedUntil',
                value: null
              })
            );
            dispatch(
              updateCollectionByPath({
                path: 'security.wrongPasswordTries',
                value: null
              })
            );
          }}
        >
          <FaUndo style={{ marginLeft: '10px' }} id="reset-password-lock" />
          <UncontrolledTooltip placement="right" target="reset-password-lock">
            {getTranslationKey('collectionSettings.resetPasswordLockTooltip')}
          </UncontrolledTooltip>
        </a>
      )}
    </div>
  );
};

export const SettingsSection = () => {
  const dispatch = useDispatch();
  const collection = useSelector(getCollectionSettings);
  const messages = useSelector(getMessages);
  const brands = useSelector(getBrandsList);
  const { type, vst } = useSelector(getUser);
  const isDownloadDisabled = type === 'VST' && get(vst, 'plan', 'basic') !== 'ultimate';
  const {
    title,
    name,
    eventDate = null,
    password,
    language,
    showAppInstall,
    sharing,
    comment,
    showImageCaptionInGallery,
    downloadPinActive,
    downloadPin,
    globalDownloadState,
    globalWatermarkID,
    expirationDate,
    faceRecognition,
    messages: collectionMessages = []
  } = collection;
  const { changeFormHandler, saveFieldHandler, changeWithUpdate } =
    useCollectionSettingsFieldUpdate();
  const { changeDownload } = useSettingsGlobalDownload();
  const { updatePin } = useSettingsDownloadPin();
  const {
    watermarkModal,
    showWatermarkModal,
    hideWatermarkModal,
    changeWatermark,
    changeWatermarkToggle
  } = useSettingsGlobalWatermark();
  const {
    faceRecognitionModalIsOpen,
    handleFaceRecognitionSwitch,
    onFaceRecognitionModalConfirm,
    closeFaceRecognitionModal
  } = useSettingsFaceRecognition();

  const changeMessagesHandler = (value: string) => {
    changeFormHandler('messages', value ? [value] : []);
  };

  useEffect(() => {
    dispatch(getPin());
    dispatch(fetchMessagesAction());
  }, []); // eslint-disable-line

  // NOTE: intentionally does NOT call moment.locale() from the collection's
  // `language` field. That set the GLOBAL react-widgets locale from a per-gallery
  // setting, so the admin date pickers/calendar followed the collection instead
  // of the account language. The account language now drives moment.locale
  // centrally (see index.tsx).

  const languageOptions = Object.keys(getTranslationKey('languages') || {}).map((key) => ({
    value: key,
    text: getTranslationKey(`languages.${key}`)
  }));

  const { isDisableModalOpen, handleCommentSwitch, closeDisableModal, confirmDisable } =
    useCommentsToggle({ changeWithUpdate });

  const brandsOptions = Object.keys(brands).map((key) => ({
    value: key,
    text: get(brands, `${key}.name`, '')
  }));

  // Display the stored instant in LOCAL time. `.utc(false)` rendered it in UTC,
  // so a date picked at local midnight (saved via toISOString as the previous
  // day 22:00Z in +offset zones) came back showing one day earlier.
  const datePickerDate = eventDate ? moment(eventDate).local().format('YYYY-MM-DDTHH:mm:ss') : '';
  const expirationDatePicker = expirationDate
    ? moment(expirationDate).format('YYYY-MM-DDTHH:mm:ss')
    : '';

  return (
    <Wrapper sm={12} md={12} lg={4}>
      <InputItem
        value={title}
        placeholder={getTranslationKey('collection.title')}
        withTooltip
        label={getTranslationKey('collection.title')}
        id="collection-edit-title"
        tooltipText={getTranslationKey('collectionTitleTooltip')}
        changeCallback={partial(changeFormHandler, 'title')}
        onBlurCallback={partial(saveFieldHandler, 'title')}
      />
      <InputItem
        value={name}
        placeholder={getTranslationKey('collection.name')}
        withTooltip
        label={getTranslationKey('collection.name')}
        id="collection-edit-inter"
        tooltipText={getTranslationKey('interNameToolTip')}
        changeCallback={partial(changeFormHandler, 'name')}
        onBlurCallback={partial(saveFieldHandler, 'name')}
      />
      <DatePicker
        date={datePickerDate}
        callback={partial(changeWithUpdate, 'eventDate')}
        placeholder={getTranslationKey('eventDate')}
        label={getTranslationKey('collectionSettings.galleryEventDate')}
      />
      <DatePicker
        date={expirationDatePicker}
        callback={partial(changeWithUpdate, 'expirationDate')}
        placeholder={getTranslationKey('collectionShop.expiresAt')}
        label={getTranslationKey('collectionShop.expiresAt')}
        id="expirationDate-date-picker"
        withTooltip
        tooltipText={getTranslationKey('collectionSettings.expirationDateTooltip')}
      />
      <InputItem
        value={password ?? ''}
        placeholder={getTranslationKey('sitePassword')}
        withTooltip
        label={<PasswordInputLabel />}
        id="collection-edit-pass"
        tooltipText={getTranslationKey('sitePasswordDesc')}
        disabled={password === null}
        errorText={password === null ? getTranslationKey('sitePasswordDecryptionError') : undefined}
        changeCallback={(value) => changeFormHandler('password', value.trimStart())}
        onBlurCallback={() => {
          if (password === null) return;
          changeFormHandler('password', password.trim());
          saveFieldHandler('password');
        }}
      />
      <SelectItem
        label={getTranslationKey('userBrand.myBrands')}
        value={collection.userBrand}
        changeCallback={partial(changeFormHandler, 'userBrand')}
        options={brandsOptions}
        onBlur={partial(saveFieldHandler, 'userBrand')}
      />
      <GalleryTagsInput sourceSection="collection" />
      <SelectItem
        label={getTranslationKey('selectLanguage')}
        value={language}
        changeCallback={partial(changeFormHandler, 'language')}
        options={languageOptions}
        onBlur={partial(saveFieldHandler, 'language')}
      />
      <DomainSelection />
      <SelectItem
        label={getTranslationKey('messages.message')}
        value={collectionMessages[0] || ''}
        changeCallback={changeMessagesHandler}
        onBlur={partial(saveFieldHandler, 'messages')}
        options={messages.map((message) => ({
          value: message.id,
          text: message.title
        }))}
        defaultOptionsText={getTranslationKey('pleaseSelect')}
      />
      <SwitchItem
        label={getTranslationKey('collectionSettings.appInstallHint')}
        value={showAppInstall}
        changeCallback={partial(changeWithUpdate, 'showAppInstall')}
        withTooltip
        tooltipPlacement="top"
        tooltipText={getTranslationKey('collectionSettings.appInstallHintTooltip')}
        id="collection-edit-app-install"
        direction="row-reverse"
      />

      <SwitchItem
        label={getTranslationKey('collectionSettings.sharing')}
        value={sharing.show}
        changeCallback={partial(changeWithUpdate, 'sharing.show')}
        withTooltip
        tooltipPlacement="top"
        tooltipText={getTranslationKey('collectionSettings.sharingTooltip')}
        id="collection-edit-app-sharing"
        direction="row-reverse"
      />
      <SwitchItem
        label={getTranslationKey('collectionSettings.comment')}
        value={comment}
        changeCallback={handleCommentSwitch}
        withTooltip
        tooltipPlacement="top"
        tooltipText={getTranslationKey('collectionSettings.commentTooltip')}
        id="collection-edit-app-comment"
        direction="row-reverse"
      />
      <SwitchItem
        label={getTranslationKey('collectionSettings.showImageCaptionInGallery')}
        value={showImageCaptionInGallery}
        changeCallback={partial(changeWithUpdate, 'showImageCaptionInGallery')}
        withTooltip
        tooltipPlacement="top"
        tooltipText={getTranslationKey('collectionSettings.showImageCaptionInGalleryTooltip')}
        id="collection-edit-app-file-names"
        direction="row-reverse"
      />
      <SwitchItem
        value={globalDownloadState}
        isDisabled={isDownloadDisabled}
        changeCallback={changeDownload}
        label={getTranslationKey('collectionSettings.download')}
        direction="row-reverse"
        margin="15px 0 20px 0"
      />
      <SwitchItem
        label={getTranslationKey('collectionSettings.downloadPin')}
        isDisabled={isDownloadDisabled}
        value={downloadPinActive}
        changeCallback={updatePin}
        withTooltip
        tooltipPlacement="top"
        tooltipText={getTranslationKey('collectionSettings.downloadPinTooltip')}
        id="collection-edit-app-pin"
        direction="row-reverse"
        margin="0 0 5px 0"
      />
      {downloadPin && downloadPin !== '0' ? (
        <div>
          <small className="text-muted">PIN: {downloadPin}</small>
        </div>
      ) : null}
      <SwitchContainer isOn={!!globalWatermarkID}>
        <SwitchItem
          value={!!globalWatermarkID}
          changeCallback={changeWatermarkToggle}
          label={getTranslationKey('watermark.watermark')}
          direction="row-reverse"
          margin="15px 0 20px 0"
        />
        <PenIcon onClick={showWatermarkModal} />
      </SwitchContainer>
      <SwitchItem
        isDisabled={faceRecognition?.lockedForChange}
        value={faceRecognition.enabled}
        changeCallback={handleFaceRecognitionSwitch}
        label={getTranslationKey('collectionSettings.shouldCollectFaceData')}
        direction="row-reverse"
        tooltipText={
          faceRecognition?.lockedForChange && !faceRecognition.enabled
            ? getTranslationKey('faceRecognition.switchDisabledHint')
            : getTranslationKey('faceRecognition.dataProcessingInfo')
        }
        isIconTooltip
        withTooltip
      />

      <SelectWatermarkModal
        isOpen={watermarkModal}
        setWatermarkCallback={changeWatermark}
        selectedWatermark={globalWatermarkID}
        onClose={hideWatermarkModal}
      />
      <TurnOffFaceRecognitionModal
        isOpen={faceRecognitionModalIsOpen}
        closeCallback={closeFaceRecognitionModal}
        confirmCallback={onFaceRecognitionModalConfirm}
      />
      <DisableCommentsModal
        isOpen={isDisableModalOpen}
        closeCallback={closeDisableModal}
        confirmCallback={confirmDisable}
      />
    </Wrapper>
  );
};
